import React, {FC, KeyboardEvent, useContext, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {Alert, Button, Input, Space, Typography, App} from "antd";
import {CheckCircleOutlined, CloseCircleOutlined, DownloadOutlined, PrinterOutlined, ScanOutlined} from "@ant-design/icons";
import {useSelectedEventWorkspace} from "./EventWorkspaceLayout";
import {AuthContext} from "../../Contexts/AuthContext";
import {downloadResponseFile, readDownloadErrorMessage} from "../../downloadFile";
import {authorizationHeaders} from "../../api/authorization";
import {StatusTag} from "../../Components/ui/StatusTag";
import type {StatusTone} from "../../Components/ui/StatusTag";
import {FieldTile, PageHeader, Panel} from "../../Components/ui";
import "../../Styles/EventCheckInStationPage.css";

type CheckInEnumValue = number | string | null | undefined;

type EventCheckInScanResult = {
    outcome?: CheckInEnumValue;
    isEligibleForPrint?: boolean;
    blockedReason?: string | null;
    eventName?: string | null;
    badgeVisualReference?: string | null;
    registrationId?: number | null;
    registrationStatus?: CheckInEnumValue;
    participantId?: number | null;
    participantFullName?: string | null;
    participantCompany?: string | null;
    country?: string | null;
    participantLinkedInUrl?: string | null;
};

type EventCheckInPrintResult = {
    printJobId?: number | null;
    status?: CheckInEnumValue;
    didTransitionToRegistriran?: boolean;
    failureMessage?: string | null;
    registrationStatusBefore?: CheckInEnumValue;
    registrationStatusAfter?: CheckInEnumValue;
    stationIdentifier?: string | null;
};

const CHECK_IN_STATION_DEFAULT = "front-desk";

const trimValue = (value?: string | null) => value?.trim() ?? "";
const encodeQueryValue = (value: string) => encodeURIComponent(value);

export type CheckInTokenParseResult =
    | {status: "valid"; token: string}
    | {status: "empty"}
    | {status: "invalid-url"};

export const parseCheckInTokenInput = (value: string): CheckInTokenParseResult => {
    const input = trimValue(value);
    if (!input) {
        return {status: "empty"};
    }

    if (!/^https?:\/\//i.test(input)) {
        return {status: "valid", token: input};
    }

    try {
        const token = trimValue(new URL(input).searchParams.get("token"));
        return token
            ? {status: "valid", token}
            : {status: "invalid-url"};
    } catch {
        return {status: "invalid-url"};
    }
};

export const buildEventCheckInScanUrl = (eventId: number | string, token: string) =>
    `/api/events/${encodeURIComponent(String(eventId))}/check-in/registration-code/scan?token=${encodeQueryValue(token)}`;

export const buildEventCheckInPrintSuccessUrl = (eventId: number | string) =>
    `/api/events/${encodeURIComponent(String(eventId))}/check-in/registration-code/print-success`;

export const buildEventCheckInPrintFailedUrl = (eventId: number | string) =>
    `/api/events/${encodeURIComponent(String(eventId))}/check-in/registration-code/print-failed`;

export const buildEventCheckInBadgeUrl = (eventId: number | string, token: string) =>
    `/api/events/${encodeURIComponent(String(eventId))}/check-in/registration-code/badge?token=${encodeQueryValue(token)}`;

const readApiPayload = async (response: Response): Promise<any> => {
    const body = await response.text();
    if (!body) {
        return null;
    }

    try {
        return JSON.parse(body);
    } catch {
        return body;
    }
};

export const extractCheckInApiErrorMessage = (payload: any, fallback: string): string => {
    if (typeof payload === "string" && payload.trim()) {
        return payload.trim();
    }

    if (Array.isArray(payload?.errors)) {
        const firstError = payload.errors
            .map((error: any) => typeof error === "string" ? error : error?.message)
            .find((value: unknown) => typeof value === "string" && value.trim());

        if (firstError) {
            return firstError.trim();
        }
    }

    if (payload?.errors && typeof payload.errors === "object") {
        const firstError = Object.values(payload.errors)
            .flat()
            .find((value: unknown) => typeof value === "string" && value.trim());

        if (typeof firstError === "string") {
            return firstError.trim();
        }
    }

    return trimValue(payload?.message)
        || trimValue(payload?.detail)
        || trimValue(payload?.title)
        || fallback;
};

const fetchJson = async (url: string, init?: RequestInit) => {
    const response = await fetch(url, init);
    const payload = await readApiPayload(response);

    if (!response.ok) {
        throw new Error(extractCheckInApiErrorMessage(payload, response.statusText || "Request failed."));
    }

    return payload;
};

const normalizeEnumText = (value: CheckInEnumValue): string => {
    if (value == null) {
        return "";
    }

    return String(value).trim();
};

export const normalizeCheckInOutcome = (value: CheckInEnumValue): string => {
    const text = normalizeEnumText(value).toLowerCase();

    switch (text) {
        case "0":
        case "invalidcode":
        case "invalid_code":
            return "invalid";
        case "1":
        case "wrongevent":
        case "wrong_event":
            return "wrong-event";
        case "2":
        case "revoked":
            return "revoked";
        case "3":
        case "blocked":
            return "blocked";
        case "4":
        case "eligibleforprint":
        case "eligible_for_print":
            return "eligible";
        default:
            return text || "unknown";
    }
};

export const normalizeRegistrationStatus = (value: CheckInEnumValue): string => {
    const text = normalizeEnumText(value).toLowerCase();

    switch (text) {
        case "0":
        case "prijavljen":
            return "Prijavljen";
        case "1":
        case "odbijen":
            return "Odbijen";
        case "2":
        case "pozvan":
            return "Pozvan";
        case "3":
        case "registriran":
            return "Registriran";
        case "4":
        case "potvrden":
        case "potvrđen":
            return "Potvrden";
        default:
            return normalizeEnumText(value) || "-";
    }
};

export const normalizePrintStatus = (value: CheckInEnumValue): string => {
    const text = normalizeEnumText(value).toLowerCase();

    switch (text) {
        case "0":
        case "failed":
            return "failed";
        case "1":
        case "succeeded":
            return "succeeded";
        default:
            return text || "unknown";
    }
};

const outcomeTone = (outcome: string): StatusTone => outcome === "eligible" ? "success" : "error";

const responseErrorMessage = (error: unknown, fallback: string) =>
    error instanceof Error && error.message ? error.message : fallback;

export const EventCheckInStationPage: FC = () => {
    const {message} = App.useApp();
    const {t} = useTranslation();
    const {event} = useSelectedEventWorkspace();
    const {auth} = useContext(AuthContext);
    const [token, setToken] = useState("");
    const [scannedToken, setScannedToken] = useState("");
    const [stationIdentifier, setStationIdentifier] = useState(CHECK_IN_STATION_DEFAULT);
    const [failureMessage, setFailureMessage] = useState("");
    const [scanResult, setScanResult] = useState<EventCheckInScanResult>();
    const [printResult, setPrintResult] = useState<EventCheckInPrintResult>();
    const [errorMessage, setErrorMessage] = useState("");
    const [scanning, setScanning] = useState(false);
    const [downloadingBadge, setDownloadingBadge] = useState(false);
    const [submittingPrintSuccess, setSubmittingPrintSuccess] = useState(false);
    const [submittingPrintFailure, setSubmittingPrintFailure] = useState(false);

    const outcome = normalizeCheckInOutcome(scanResult?.outcome);
    const printStatus = normalizePrintStatus(printResult?.status);
    const stationName = trimValue(stationIdentifier);
    const canReportPrintResult = !!scanResult?.isEligibleForPrint && !!scannedToken && !!stationName;
    const badgeStatus = useMemo(() => trimValue(scanResult?.badgeVisualReference)
        ? t("check-in.badge-ready", {defaultValue: "Ready"})
        : t("check-in.badge-missing", {defaultValue: "Missing"}), [scanResult?.badgeVisualReference, t]);

    const outcomeLabel = useMemo(() => {
        switch (outcome) {
            case "eligible":
                return t("check-in.outcome.eligible", {defaultValue: "Eligible for print"});
            case "invalid":
                return t("check-in.outcome.invalid", {defaultValue: "Invalid code"});
            case "wrong-event":
                return t("check-in.outcome.wrong-event", {defaultValue: "Wrong event"});
            case "revoked":
                return t("check-in.outcome.revoked", {defaultValue: "Code revoked"});
            case "blocked":
                return t("check-in.outcome.blocked", {defaultValue: "Not eligible"});
            default:
                return t("check-in.outcome.unknown", {defaultValue: "Unknown status"});
        }
    }, [outcome, t]);

    const printStatusLabel = useMemo(() => {
        switch (printStatus) {
            case "succeeded":
                return t("check-in.print-succeeded", {defaultValue: "Print succeeded"});
            case "failed":
                return t("check-in.print-failed", {defaultValue: "Print failed"});
            default:
                return t("check-in.print-status", {defaultValue: "Print status"});
        }
    }, [printStatus, t]);

    const handleScan = async () => {
        const parsedToken = parseCheckInTokenInput(token);
        if (parsedToken.status === "empty") {
            message.error(t("check-in.token-required", {defaultValue: "Registration code token is required."}));
            return;
        }
        if (parsedToken.status === "invalid-url") {
            message.error(t("check-in.token-url-invalid", {
                defaultValue: "Scanned URL does not contain a registration code token."
            }));
            return;
        }

        setScanning(true);
        setErrorMessage("");
        setPrintResult(undefined);
        setScannedToken("");

        try {
            const result = await fetchJson(buildEventCheckInScanUrl(event.id, parsedToken.token), {
                credentials: "include",
                headers: authorizationHeaders(auth.token)
            });
            setScanResult(result);
            setScannedToken(parsedToken.token);
        } catch (error) {
            setScanResult(undefined);
            setErrorMessage(responseErrorMessage(error, t("check-in.scan-failed", {defaultValue: "Registration code could not be scanned."})));
        } finally {
            setScanning(false);
        }
    };

    const handlePrintSuccess = async () => {
        if (!canReportPrintResult) {
            message.error(t("check-in.print-result-missing", {defaultValue: "Scan an eligible code and enter a station name first."}));
            return;
        }

        setSubmittingPrintSuccess(true);
        setErrorMessage("");

        try {
            const result = await fetchJson(buildEventCheckInPrintSuccessUrl(event.id), {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                    ...authorizationHeaders(auth.token)
                },
                body: JSON.stringify({
                    token: scannedToken,
                    stationIdentifier: stationName
                })
            });
            setPrintResult(result);
            setScanResult(previous => previous
                ? {
                    ...previous,
                    isEligibleForPrint: false,
                    registrationStatus: result?.registrationStatusAfter ?? previous.registrationStatus
                }
                : previous);
            message.success(t("check-in.print-success-recorded", {defaultValue: "Print success recorded."}));
        } catch (error) {
            setErrorMessage(responseErrorMessage(error, t("check-in.print-success-failed", {defaultValue: "Print success could not be recorded."})));
        } finally {
            setSubmittingPrintSuccess(false);
        }
    };

    const handleDownloadBadge = async () => {
        if (!scanResult?.isEligibleForPrint || !scannedToken) {
            message.error(t("check-in.badge-download-missing", {defaultValue: "Scan an eligible code first."}));
            return;
        }

        setDownloadingBadge(true);
        setErrorMessage("");
        try {
            const response = await fetch(buildEventCheckInBadgeUrl(event.id, scannedToken), {
                credentials: "include",
                headers: authorizationHeaders(auth.token)
            });
            if (!response.ok) {
                throw new Error(await readDownloadErrorMessage(
                    response,
                    t("check-in.badge-download-failed", {defaultValue: "Badge could not be generated."})));
            }

            await downloadResponseFile(response, `Badge_${scanResult.registrationId ?? event.id}.pdf`);
            message.success(t("check-in.badge-downloaded", {defaultValue: "Print-ready badge downloaded."}));
        } catch (error) {
            setErrorMessage(responseErrorMessage(
                error,
                t("check-in.badge-download-failed", {defaultValue: "Badge could not be generated."})));
        } finally {
            setDownloadingBadge(false);
        }
    };

    const handlePrintFailed = async () => {
        if (!canReportPrintResult) {
            message.error(t("check-in.print-result-missing", {defaultValue: "Scan an eligible code and enter a station name first."}));
            return;
        }

        const stationFailureMessage = trimValue(failureMessage);
        if (!stationFailureMessage) {
            message.error(t("check-in.failure-message-required", {defaultValue: "Print failure note is required."}));
            return;
        }

        setSubmittingPrintFailure(true);
        setErrorMessage("");

        try {
            const result = await fetchJson(buildEventCheckInPrintFailedUrl(event.id), {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                    ...authorizationHeaders(auth.token)
                },
                body: JSON.stringify({
                    token: scannedToken,
                    stationIdentifier: stationName,
                    failureMessage: stationFailureMessage
                })
            });
            setPrintResult(result);
            message.success(t("check-in.print-failure-recorded", {defaultValue: "Print issue recorded."}));
        } catch (error) {
            setErrorMessage(responseErrorMessage(error, t("check-in.print-failure-failed", {defaultValue: "Print issue could not be recorded."})));
        } finally {
            setSubmittingPrintFailure(false);
        }
    };

    const handleTokenKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            void handleScan();
        }
    };

    return (
        <div className="ati-stack">
            <PageHeader level={2} title={t("check-in.station-title", {defaultValue: "Check-in station"})}/>

            <Panel>
                <div className="ati-stack">
                <div className="check-in-station__grid">
                    <div>
                        <Typography.Text strong>
                            {t("check-in.registration-code-token", {defaultValue: "Registration code token"})}
                        </Typography.Text>
                        <Input
                            aria-label={t("check-in.registration-code-token", {defaultValue: "Registration code token"})}
                            autoComplete="off"
                            autoFocus
                            value={token}
                            onChange={event => setToken(event.target.value)}
                            onKeyDown={handleTokenKeyDown}
                        />
                    </div>
                    <div>
                        <Typography.Text strong>
                            {t("check-in.station-identifier", {defaultValue: "Station name"})}
                        </Typography.Text>
                        <Input
                            aria-label={t("check-in.station-identifier", {defaultValue: "Station name"})}
                            value={stationIdentifier}
                            onChange={event => setStationIdentifier(event.target.value)}
                        />
                    </div>
                </div>
                <div>
                    <Button
                        type="primary"
                        icon={<ScanOutlined/>}
                        loading={scanning}
                        onClick={handleScan}
                    >
                        {t("check-in.scan", {defaultValue: "Scan code"})}
                    </Button>
                </div>
                </div>
            </Panel>

            {errorMessage ? (
                <Alert type="error" message={t("check-in.station-error", {defaultValue: "Check-in station error"})} description={errorMessage}/>
            ) : null}

            <div aria-live="polite">
            {scanResult ? (
                <Panel>
                    <div className="ati-stack">
                    <Space wrap>
                        <StatusTag tone={outcomeTone(outcome)}>{outcomeLabel}</StatusTag>
                        <StatusTag tone={scanResult.isEligibleForPrint ? "success" : "neutral"}>{badgeStatus}</StatusTag>
                        <StatusTag>{normalizeRegistrationStatus(scanResult.registrationStatus)}</StatusTag>
                    </Space>

                    <div className="check-in-station__details">
                        <FieldTile label={t("participant", {defaultValue: "Participant"})}>
                            <Typography.Text strong>{scanResult.participantFullName || "-"}</Typography.Text>
                        </FieldTile>
                        <FieldTile label={t("company", {defaultValue: "Company"})}>
                            {scanResult.participantCompany || "-"}
                        </FieldTile>
                        <FieldTile label={t("country", {defaultValue: "Country"})}>
                            {scanResult.country || "-"}
                        </FieldTile>
                        <FieldTile label={t("registration", {defaultValue: "Registration"})}>
                            {scanResult.registrationId ?? "-"}
                        </FieldTile>
                    </div>

                    {scanResult.blockedReason ? (
                        <Alert type="warning" message={scanResult.blockedReason}/>
                    ) : null}

                    <div className="ati-stack ati-stack--tight">
                        <Space wrap>
                            <Button
                                type="primary"
                                icon={<DownloadOutlined/>}
                                disabled={!scanResult.isEligibleForPrint || !scannedToken}
                                loading={downloadingBadge}
                                onClick={handleDownloadBadge}
                            >
                                {t("check-in.download-badge", {defaultValue: "Download badge"})}
                            </Button>
                            <Button
                                icon={<CheckCircleOutlined/>}
                                disabled={!canReportPrintResult}
                                loading={submittingPrintSuccess}
                                onClick={handlePrintSuccess}
                            >
                                {t("check-in.report-print-success", {defaultValue: "Print succeeded"})}
                            </Button>
                            <Button
                                danger
                                icon={<CloseCircleOutlined/>}
                                disabled={!canReportPrintResult}
                                loading={submittingPrintFailure}
                                onClick={handlePrintFailed}
                            >
                                {t("check-in.report-print-failed", {defaultValue: "Record print issue"})}
                            </Button>
                        </Space>
                        <Input.TextArea
                            aria-label={t("check-in.failure-message", {defaultValue: "Print failure note"})}
                            value={failureMessage}
                            onChange={event => setFailureMessage(event.target.value)}
                            rows={3}
                        />
                    </div>
                    </div>
                </Panel>
            ) : null}
            </div>

            {printResult ? (
                <Alert
                    type={printStatus === "succeeded" ? "success" : "warning"}
                    icon={<PrinterOutlined/>}
                    showIcon
                    message={printStatusLabel}
                    description={printResult.didTransitionToRegistriran
                        ? t("check-in.registration-updated", {defaultValue: "Registration moved to Registriran."})
                        : (printResult.failureMessage || t("check-in.print-recorded", {defaultValue: "Print result recorded."}))}
                />
            ) : null}
        </div>
    );
};

export default EventCheckInStationPage;
